import { useState } from 'react';

const faqs = [
  {
    id: 1,
    question: "What's included with every booking?",
    answer:
      "Every reservation includes a professional, uniformed chauffeur, a luxury BMW sedan (740i or 530i), climate-controlled interior, premium leather seating, and complimentary bottled water. You get a white-glove experience from door to door — no hidden fees.",
  },
  {
    id: 2,
    question: "Where do you provide service?",
    answer:
      "We operate exclusively in Philadelphia, PA and the surrounding Greater Philadelphia area. Whether your prom venue is in Center City, the Main Line, or the suburbs, we've got you covered. Not sure if your location qualifies? Reach out and we'll confirm.",
  },
  {
    id: 3,
    question: "What is the cancellation policy?",
    answer:
      "All bookings require a $500 non-refundable deposit to secure your date. If you need to cancel, the deposit will not be returned. Cancellations made less than 7 days before your prom date forfeit the full payment. We strongly recommend booking early and confirming your plans before reserving.",
  },
  {
    id: 4,
    question: "When is full payment due?",
    answer:
      "Full payment must be completed no later than one week (7 days) prior to your booking date. Failure to complete payment by this deadline may result in your reservation being released. You'll receive a reminder — but it's your responsibility to ensure payment is made on time.",
  },
  {
    id: 5,
    question: "How far in advance should I book?",
    answer:
      "Prom season is our busiest time of year and dates fill up fast. We strongly recommend booking as early as possible — ideally 4–8 weeks in advance. Once your $500 deposit is paid, your date is locked in and guaranteed.",
  },
  {
    id: 6,
    question: "How many passengers can ride?",
    answer:
      "The 2026 BMW 740i accommodates up to 4 passengers, while the 2026 BMW 530i fits up to 3 passengers comfortably. All passengers must be seated with seatbelts fastened at all times for safety.",
  },
  {
    id: 7,
    question: "Can I customize my pickup and drop-off locations?",
    answer:
      "Absolutely. During booking, you'll provide your pickup address and venue/destination. We'll map out the route in advance so the evening runs seamlessly. For round-trip bookings, we'll also coordinate your return pickup time.",
  },
  {
    id: 8,
    question: "Is there a difference between Drop-Off Only and Round Trip?",
    answer:
      "Drop-Off Only means we chauffeur you from your pickup address to your prom venue — one way. Round Trip includes both the arrival pickup and a return trip at the end of the night. Round trip pricing reflects the added time and coordination involved.",
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section id="faq" className="py-28 bg-[#070707] relative">
      <div className="section-divider mb-20"></div>

      <div className="max-w-3xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-16">
          <p className="text-gray-500 text-xs tracking-[0.35em] uppercase mb-4">Got Questions?</p>
          <h2 className="font-display silver-gradient-text text-5xl md:text-6xl font-semibold tracking-wide mb-6">
            FAQ
          </h2>
          <p className="text-gray-400 text-sm tracking-wide max-w-xl mx-auto leading-relaxed">
            Everything you need to know about booking your Philadelphia luxury prom chauffeur experience.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`card-luxury rounded-xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-gray-600' : 'border-gray-800 hover:border-gray-700'}`}
              >
                <button
                  onClick={() => toggle(faq.id)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer group"
                >
                  <span className={`font-display text-sm md:text-base tracking-wide leading-snug transition-colors duration-200 ${isOpen ? 'silver-gradient-text' : 'text-gray-300 group-hover:text-white'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center shrink-0 rounded-full border transition-all duration-300 ${isOpen ? 'border-gray-500 bg-white/5 rotate-45' : 'border-gray-700 bg-transparent'}`}>
                    <i className="ri-add-line text-gray-400 text-sm"></i>
                  </div>
                </button>

                {/* Answer */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-6 pb-6">
                    <div className="w-full h-px bg-gray-800 mb-4"></div>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-[#111111] border border-gray-800 rounded-2xl p-8">
          <div className="w-12 h-12 flex items-center justify-center silver-border rounded-full mx-auto mb-4">
            <i className="ri-question-answer-line silver-gradient-text-sm text-xl"></i>
          </div>
          <p className="text-white font-display text-lg font-medium mb-2">Still have questions?</p>
          <p className="text-gray-500 text-sm mb-6 leading-relaxed">
            Don't hesitate to reach out — we're happy to walk you through everything before you book.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block btn-silver rounded-xl px-8 py-3 text-xs tracking-[0.15em] uppercase cursor-pointer whitespace-nowrap"
          >
            Contact Us
          </a>
        </div>
      </div>

      <div className="section-divider mt-20"></div>
    </section>
  );
}